import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Topbar from '../../components/common/Topbar';
import { updateMarketData } from '../../store/tradeSlice';
import { fmtUSD } from '../../utils/formatters';
import { INSTRUMENTS, getLotSize } from '../../utils/constants';
import { calcForexPnl } from '../../utils/forexUtils';
import api from '../../services/api';

const calcPositionPnl = (pos, currentPrice) => {
  const ls = getLotSize(pos.category || 'crypto');
  const qty = Math.abs(Number(pos.qty)) || 0;
  const avgPrice = Number(pos.avgPrice) || 0;
  const isShort = pos.direction === 'sell' || Number(pos.qty) < 0;
  return calcForexPnl(isShort, currentPrice, avgPrice, qty, pos.instrumentId, pos.category || 'crypto', ls);
};

const Home = () => {
  const dispatch = useDispatch();
  const { balance, portfolio, historyOrders, marketData } = useSelector(s => s.trade);
  const { user } = useSelector(s => s.auth);

  const [totalDeposits,    setTotalDeposits]    = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [totalProfit,      setTotalProfit]      = useState(0);
  const [totalCredit,      setTotalCredit]      = useState(0);
  const [bonusBalance,     setBonusBalance]     = useState(0);
  const [recentActivity,   setRecentActivity]   = useState([]);
  const [isLoading,        setIsLoading]        = useState(true);

  const tickTimerRef = useRef(null);

  const fetchMarketData = async () => {
    try {
      const symbols = INSTRUMENTS.map(i => i.id).join(',');
      const response = await api.get(`/market/quotes?symbols=${symbols}`);
      const raw = response.data;
      const dataMap = {};
      if (Array.isArray(raw)) {
        raw.forEach(q => { if (q?.symbol) dataMap[q.symbol] = q; });
      } else if (raw && typeof raw === 'object') {
        Object.entries(raw).forEach(([sym, q]) => {
          if (q?.price) dataMap[sym] = { ...q, symbol: sym };
        });
      }
      if (Object.keys(dataMap).length > 0) {
        dispatch(updateMarketData(dataMap));
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchMarketData();
    tickTimerRef.current = setInterval(fetchMarketData, 3000);
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, []);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [activityRes, financeRes, creditRes] = await Promise.all([
        api.get('/user/activity/recent'),
        api.get('/user/finance/summary'),
        api.get('/user/credit/requests'),
      ]);
      
      setRecentActivity((activityRes.data || []).slice(0, 10));
      setTotalDeposits(Number(financeRes.data?.totalDeposits) || 0);
      setTotalWithdrawals(Number(financeRes.data?.totalWithdrawals) || 0);
      setTotalProfit(Number(financeRes.data?.totalProfit) || 0);
      setBonusBalance(Number(financeRes.data?.bonusBalance) || 0);
      
      if (creditRes.data && Array.isArray(creditRes.data)) {
        const approved = creditRes.data
          .filter(r => r.status === 'approved')
          .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
        setTotalCredit(approved);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setTotalDeposits(Number(user?.totalDeposits) || 0);
      setTotalWithdrawals(Number(user?.totalWithdrawals) || 0);
      setBonusBalance(Number(user?.bonusBalance) || 0);
    } finally {
      setIsLoading(false);
    }
  };

  const positions = Object.entries(portfolio).map(([id, pos]) => pos);

  const calculateOpenPnl = () => {
    let pnl = 0;
    positions.forEach(pos => {
      const instrumentId = pos.instrumentId;
      const currentPrice = marketData[instrumentId]?.price || Number(pos.avgPrice) || 0;
      pnl += calcPositionPnl(pos, currentPrice);
    });
    return pnl;
  };

  const calculateMarginUsed = () => {
    let margin = 0;
    positions.forEach(pos => {
      margin += Number(pos.cost) || 0;
    });
    return margin;
  };

  const openPnl = calculateOpenPnl();
  const marginUsed = calculateMarginUsed();

  const creditLimit = Number(user?.creditLimit) || 0;
  const displayBonusBalance = Number(bonusBalance) || 0;

  const creditUsed = Math.min(
    creditLimit,
    Math.max(0, marginUsed - balance - displayBonusBalance)
  );

  const creditAvailable = creditLimit - creditUsed;

  const equity = balance + openPnl + totalCredit + displayBonusBalance;
  const freeMargin = equity - marginUsed;
  const marginLevel = marginUsed > 0 ? (equity / marginUsed) * 100 : 0;

  const totalSwap = historyOrders.reduce((s, o) => s + (Number(o.swap) || 0), 0);
  const closedPnl = historyOrders.reduce((sum, o) => sum + (Number(o.pnl) || 0), 0);

  const closedTrades  = historyOrders.filter(o => o);
  const winningTrades = closedTrades.filter(o => (Number(o.pnl) || 0) > 0);
  const winRate = closedTrades.length > 0
    ? (winningTrades.length / closedTrades.length * 100).toFixed(1)
    : 0;

  const getMarginLevelColor = () => {
    if (marginUsed === 0) return 'var(--color-textSecondary)';
    if (marginLevel > 200) return 'var(--color-greenLight)';
    if (marginLevel > 100) return 'var(--color-yellow)';
    return 'var(--color-redLight)';
  };

  const getCreditUsedColor = () => {
    if (creditLimit === 0) return 'var(--color-textSecondary)';
    const pct = (creditUsed / creditLimit) * 100;
    if (pct < 50) return 'var(--color-greenLight)';
    if (pct < 80) return 'var(--color-yellow)';
    return 'var(--color-redLight)';
  };

  const statsGrid = [
    { label: 'BALANCE',         value: fmtUSD(balance),             color: 'var(--color-text)',    icon: 'fa-coins',              iconColor: 'var(--color-text)' },
    { label: 'EQUITY',          value: fmtUSD(equity),              color: 'var(--color-text)',    icon: 'fa-scale-balanced',     iconColor: 'var(--color-purpleLight)' },
    { label: 'OPEN P&L',        value: (openPnl >= 0 ? '+' : '') + fmtUSD(openPnl),        color: openPnl >= 0 ? 'var(--color-greenLight)' : 'var(--color-redLight)', icon: 'fa-chart-line', iconColor: 'var(--color-greenLight)' },
    { label: 'CLOSED P&L',      value: (closedPnl >= 0 ? '+' : '') + fmtUSD(closedPnl),    color: closedPnl >= 0 ? 'var(--color-greenLight)' : 'var(--color-redLight)', icon: 'fa-clock-rotate-left', iconColor: 'var(--color-greenLight)' },
    { label: 'CREDIT LIMIT',    value: fmtUSD(creditLimit),         color: 'var(--color-blue)',    icon: 'fa-credit-card',        iconColor: 'var(--color-blue)' },
    { label: 'CREDIT USED',     value: fmtUSD(creditUsed),          color: getCreditUsedColor(), icon: 'fa-credit-card-alt', iconColor: 'var(--color-yellow)' },
    { label: 'CREDIT AVAIL',    value: fmtUSD(creditAvailable),     color: creditAvailable > 0 ? 'var(--color-greenLight)' : 'var(--color-redLight)', icon: 'fa-unlock', iconColor: 'var(--color-greenLight)' },
    { label: 'CREDIT GIVEN',    value: fmtUSD(totalCredit),         color: 'var(--color-text)',    icon: 'fa-hand-holding-dollar', iconColor: 'var(--color-text)' },
    { label: 'BONUS',           value: fmtUSD(displayBonusBalance), color: 'var(--color-purpleLight)',    icon: 'fa-gift',               iconColor: 'var(--color-purpleLight)' },
    { label: 'TOTAL DEPOSITS',  value: fmtUSD(totalDeposits),       color: 'var(--color-greenLight)',    icon: 'fa-circle-plus',        iconColor: 'var(--color-greenLight)' },
    { label: 'TOTAL WITHDRAWALS', value: fmtUSD(totalWithdrawals),  color: 'var(--color-redLight)',    icon: 'fa-circle-minus',       iconColor: 'var(--color-redLight)' },
    { label: 'TOTAL PROFIT',    value: (totalProfit >= 0 ? '+' : '') + fmtUSD(totalProfit), color: totalProfit >= 0 ? 'var(--color-greenLight)' : 'var(--color-redLight)', icon: 'fa-trophy', iconColor: 'var(--color-yellow)' },
    { label: 'MARGIN USED',     value: fmtUSD(marginUsed),          color: 'var(--color-yellow)',    icon: 'fa-lock',               iconColor: 'var(--color-yellow)' },
    { label: 'FREE MARGIN',     value: fmtUSD(freeMargin),          color: freeMargin >= 0 ? 'var(--color-greenLight)' : 'var(--color-redLight)', icon: 'fa-money-bill-wave', iconColor: 'var(--color-greenLight)' },
    { label: 'MARGIN LEVEL',    value: marginUsed > 0 ? marginLevel.toFixed(1) + '%' : '—', color: getMarginLevelColor(), icon: 'fa-shield-halved', iconColor: 'var(--color-purpleLight)' },
    { label: 'TOTAL SWAP',      value: '-$' + totalSwap.toFixed(2), color: 'var(--color-orangeLight)',    icon: 'fa-rotate',             iconColor: 'var(--color-yellow)' },
    { label: 'TOTAL TRADES',    value: historyOrders.length,        color: 'var(--color-text)',    icon: 'fa-fire',               iconColor: 'var(--color-text)' },
    { label: 'WIN RATE',        value: winRate + '%',               color: 'var(--color-purpleLight)',    icon: 'fa-bullseye',           iconColor: 'var(--color-purpleLight)' },
  ];

  const cardStyle = {
    background: 'var(--color-bgSurface)',
    border: '1px solid var(--color-border)',
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{ background: 'radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)' }} />
      </div>

      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 max-w-[1400px] mx-auto">

        <div className="mb-6 animate-slide-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
            style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
            <i className="fas fa-house text-[9px]" style={{ color: 'var(--color-text)' }} />
            <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-text)' }}>DASHBOARD</span>
          </div>
          <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
            Welcome back, <span style={{ color: 'var(--color-text)' }}>{user?.firstName || 'Trader'}</span>
          </h1>
          <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>Here's an overview of your trading activity and account performance.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {statsGrid.map(card => (
            <div key={card.label} className="rounded-xl p-4 transition-all hover:-translate-y-0.5" style={cardStyle}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-orangeBorder)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border)'}>
              <div className="flex items-center gap-2 mb-2">
                <i className={`fas ${card.icon} text-[11px]`} style={{ color: card.iconColor }} />
                <span className="text-[9px] font-mono tracking-wide uppercase" style={{ color: 'var(--color-textSecondary)' }}>{card.label}</span>
              </div>
              <div className="font-syne text-lg font-extrabold" style={{ color: card.color }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl overflow-hidden mb-6" style={cardStyle}>
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--color-borderMedium)' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-clock-rotate-left text-xs" />
              </div>
              <div>
                <div className="font-syne text-sm font-extrabold" style={{ color: 'var(--color-text)' }}>Recent Activity</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>Latest trades &amp; deposits</div>
              </div>
            </div>
            <Link to="/statement" className="text-[9px] font-mono hover:underline" style={{ color: 'var(--color-text)' }}>View All →</Link>
          </div>

          <div className="divide-y max-h-[400px] overflow-y-auto" style={{ borderColor: 'var(--color-borderLight)' }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--color-orange)', borderTopColor: 'transparent' }} />
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <i className="fas fa-inbox text-3xl mb-3 opacity-50" style={{ color: 'var(--color-textDim)' }} />
                <p className="font-mono text-xs" style={{ color: 'var(--color-textSecondary)' }}>No recent activity</p>
                <Link to="/deposit" className="mt-3 text-xs hover:underline" style={{ color: 'var(--color-text)' }}>Make your first deposit →</Link>
              </div>
            ) : (
              recentActivity.map((activity, idx) => {
                const isBuy     = activity.type === 'buy';
                const isDeposit = activity.type === 'deposit';
                const pnl       = Number(activity.pnl) || 0;
                return (
                  <div key={idx} className="flex items-center gap-3 p-4 transition-all" style={{ background: 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bgHover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{
                        background: isDeposit ? 'var(--color-orangeBg)' : isBuy ? 'var(--color-greenBg)' : 'var(--color-redBg)',
                        color: isDeposit ? 'var(--color-text)' : isBuy ? 'var(--color-green)' : 'var(--color-red)',
                        border: `1px solid ${isDeposit ? 'var(--color-orangeBorder)' : isBuy ? 'var(--color-greenBorder)' : 'var(--color-redBorder)'}`
                      }}>
                      <i className={`fas ${isDeposit ? 'fa-circle-plus' : isBuy ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'} text-xs`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-syne text-xs font-bold truncate" style={{ color: 'var(--color-text)' }}>
                        {isDeposit
                          ? `Deposit via ${activity.method || 'Card'}`
                          : `${activity.type?.toUpperCase()} ${activity.instrumentId || ''}`}
                      </div>
                      <div className="text-[9px] font-mono mt-0.5" style={{ color: 'var(--color-textSecondary)' }}>{activity.time}</div>
                    </div>
                    <div className="text-right font-mono text-sm font-bold shrink-0"
                      style={{
                        color: isDeposit ? 'var(--color-text)' :
                          pnl > 0 ? 'var(--color-green)' :
                          pnl < 0 ? 'var(--color-red)' : 'var(--color-textSecondary)'
                      }}>
                      {isDeposit
                        ? `+${fmtUSD(Math.abs(activity.amount || 0))}`
                        : pnl !== 0
                          ? `${pnl >= 0 ? '+' : ''}${fmtUSD(pnl)}`
                          : '—'}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <Link to="/statement" className="block text-center py-3 text-[10px] font-mono transition-all"
            style={{ color: 'var(--color-textSecondary)', borderTop: '1px solid var(--color-borderMedium)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--color-text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--color-textSecondary)'}>
            Full Statement <i className="fas fa-arrow-right ml-1 text-[8px]" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Link to="/deposit" className="flex items-center justify-center gap-2 p-3 rounded-xl font-syne text-xs font-bold transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
            <i className="fas fa-circle-plus text-[10px]" /> Deposit
          </Link>
          <Link to="/withdrawal" className="flex items-center justify-center gap-2 p-3 rounded-xl font-syne text-xs font-bold transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)', color: 'var(--color-red)' }}>
            <i className="fas fa-circle-minus text-[10px]" /> Withdraw
          </Link>
          <Link to="/transfer" className="flex items-center justify-center gap-2 p-3 rounded-xl font-syne text-xs font-bold transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--color-blueBg)', border: '1px solid var(--color-blueBorder)', color: 'var(--color-blue)' }}>
            <i className="fas fa-right-left text-[10px]" /> Transfer
          </Link>
          <Link to="/terminal" className="flex items-center justify-center gap-2 p-3 rounded-xl font-syne text-xs font-bold transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--color-purpleBg)', border: '1px solid var(--color-purpleBorder)', color: 'var(--color-purpleLight)' }}>
            <i className="fas fa-chart-candlestick text-[10px]" /> Terminal
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(28px, -28px); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-float       { animation: float 20s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: -8s; }
        .animate-slide-up    { animation: slide-up 0.6s ease-out both; }
      `}</style>
    </div>
  );
};

export default Home;